import { Box } from '@joinrepublic/dls-ui-kit';

import {
  toRwaQuote,
  useInxTickerQuery,
} from '@r-client/republic/data/offerings';
import {
  REAL_WORLD_ASSETS,
  RealWorldAssetsRail,
  SeeAllButton,
} from '@r-client/republic/ui/offerings';
import { ASSET_TYPES } from '@r-client/shared/data/analytics';

import { useHomepageOpportunitiesAnalytics } from '../../../hooks/use-homepage-opportunities-analytics';
import {
  RAIL_HEADING_LEVEL,
  SECTION_RAIL_BLEED,
  SECTION_RAIL_GUTTER,
  SEE_ALL_HREF,
} from '../constants';

/**
 * The real-world assets rail as it appears on the homepage — the same rail and
 * the same two assets as /investment-opportunities, differing in heading level,
 * LCP treatment, analytics `source`, its mobile layout, and where its "See all"
 * goes: that page's header links out to INX, whereas here the link exists on
 * mobile only and leads to /investment-opportunities.
 *
 * `isLcpCandidate` is off: the homepage's own hero is the largest thing above
 * the fold here, so preloading a card that starts well below it would delay the
 * image that actually decides LCP.
 */
export const RealWorldAssets = () => {
  const { assetCardClicked, seeAllClicked } =
    useHomepageOpportunitiesAnalytics();
  // No loading or error state to render: each card falls back to its fixture
  // price until live data arrives.
  const { tickerData } = useInxTickerQuery();

  const onSeeAllClick = () => seeAllClicked(ASSET_TYPES.RWA);

  const assets = REAL_WORLD_ASSETS.map((asset) => ({
    ...asset,
    ...toRwaQuote(tickerData, asset.symbol),
  }));

  return (
    <>
      <RealWorldAssetsRail
        assets={assets}
        headingAs={RAIL_HEADING_LEVEL}
        bleed={SECTION_RAIL_BLEED}
        gutter={SECTION_RAIL_GUTTER}
        mobileLayout="stack"
        isLcpCandidate={false}
        onAssetClick={(id) => assetCardClicked(ASSET_TYPES.RWA, id)}
      />

      {/* The section's only "See all", and mobile-only unlike the growth rail's:
          no `seeAllHref` reaches the header, so from `sm` up the rail's two cards
          stand on their own and this is what `mobileLayout="stack"` leaves in
          place of the missing terminating tile. */}
      {/* On the rail's bleed and gutter rather than the section `Container`'s
          padding: the button is a peer of the stacked cards, and the design has
          it exactly as wide. Left on the container it would be
          `SECTION_PADDING_X - SECTION_RAIL_GUTTER` (8px) narrower on each side
          than the cards above it. Both constants are zero from `sm`, so this
          only ever moves the mobile button. */}
      <Box
        display={{ base: 'block', sm: 'none' }}
        mx={SECTION_RAIL_BLEED}
        px={SECTION_RAIL_GUTTER}
        mt="4"
      >
        <SeeAllButton href={SEE_ALL_HREF} onClick={onSeeAllClick} />
      </Box>
    </>
  );
};
